import { useEffect } from 'react';
import { cast } from 'mobx-state-tree';

import { useMst } from '@trader/store';
import {
  getAccountTypeName,
  isChallengeAccountType,
  MODAL_TYPES,
  productName,
} from '@trader/constants';

import { useNavigation } from '../useNavigation';
import { useHub } from './core/useHub';
import {
  EConnectionHub,
  EConnectionHubSubscriber,
  ESocketUpdateAction,
  IMessageTradingAccount,
} from './core/types';

export const useTradingAccounts = () => {
  const store = useMst();
  const { navigateTo } = useNavigation();

  const idToken = store.auth.tokens.idToken;

  const { unsubscribe, subscribe } = useHub<IMessageTradingAccount>({
    url: `${import.meta.env.VITE_SIGNALR_URL}/inboxes`,
    hub: EConnectionHub.Inboxes,
    subscriber: EConnectionHubSubscriber.TradingAccount,
    onMessage: message => {
      if (message.updateAction === ESocketUpdateAction.Create) {
        store.user.runInAction(() => {
          store.user.tradingAccounts = cast([
            {
              platformLogin: message.platformLogin,
              accountType: getAccountTypeName[message.accountType],
              product: productName[message.product],
              currency: message.currency,
              currencySymbol: message.currencySymbol,
              tradingId: message.tradingId,
              isInUse: false,
            },
            ...store.user.tradingAccounts,
          ]);
        });
      }
      if (message.updateAction === ESocketUpdateAction.Update) {
        const activeAcc = store.user.activeTradingAcc();

        if (activeAcc.tradingId === message.tradingId) {
          store.user.runInAction(() => {
            store.user.profile.isDepositAllowed = message.isDepositAllowed;
            store.user.profile.isTradingAllowed = message.isTradingAllowed;
          });
        }
      }
      if (message.updateAction === ESocketUpdateAction.Delete) {
        const activeAcc = store.user.activeTradingAcc();

        if (activeAcc.tradingId === message.tradingId) {
          const accounts = isChallengeAccountType
            ? store.user.tradingAccounts.filter(acc => {
                const challenge = store.user.getAccountChallenge(acc.tradingId);
                return challenge?.status !== 'DISABLED';
              })
            : store.user.tradingAccounts;

          const newAccount = accounts.find(
            acc => acc.tradingId !== message.tradingId
          );

          if (!newAccount) {
            store.ui.modal.open(MODAL_TYPES.resultChallenge, {
              isActiveChallenge: false,
              shouldHideCloseButton: true,
              result: 'EmptyAccounts',
            });
            return;
          }

          store.user.changeTradingAccountAsync.run({
            platformLogin: newAccount?.platformLogin || '',
            navigation: navigateTo,
          });
        }

        store.user.runInAction(() => {
          store.user.tradingAccounts = cast(
            store.user.tradingAccounts.filter(
              acc => acc.tradingId !== message.tradingId
            )
          );
        });
      }
    },
    invokedName: 'OnTradingAccount',
    subscriptionArgs: {},
  });

  useEffect(() => {
    if (store.auth.isAuth) {
      subscribe();
    }
    return () => {
      if (!store.auth.isAuth) {
        unsubscribe();
      }
    };
  }, [store.auth.isAuth, idToken]);
};
